import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

// In-memory token store (secure — not accessible via XSS like localStorage)
let _authToken = null;
export const setAuthToken = (token) => { _authToken = token; };
export const clearAuthToken = () => { _authToken = null; };
export const getAuthToken = () => _authToken;

// Attach Bearer header from in-memory token
api.interceptors.request.use((config) => {
  if (_authToken) {
    config.headers.Authorization = `Bearer ${_authToken}`;
  }
  return config;
});

// Handle 401 errors - only redirect if not already on auth pages
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const isAuthPage = window.location.pathname === '/login' || window.location.pathname === '/register';
      const isAuthCheck = error.config?.url?.includes('/auth/me');
      if (!isAuthPage && !isAuthCheck) {
        _authToken = null;
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
};

// Users
export const usersAPI = {
  list: (params) => api.get('/users', { params }),
  get: (id) => api.get(`/users/${id}`),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// Clients
export const clientsAPI = {
  list: () => api.get('/clients'),
  get: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  deleteAll: () => api.delete('/clients'),
  listDirectors: (id) => api.get(`/clients/${id}/directors`),
  linkDirector: (id, data) => api.post(`/clients/${id}/directors`, data),
  unlinkDirector: (id, directorId) => api.delete(`/clients/${id}/directors/${directorId}`),
  updateDirectorLink: (id, directorId, data) => api.put(`/clients/${id}/directors/${directorId}`, data),
};

// Directors / Designated Partners
export const directorsAPI = {
  list: (search) => api.get('/directors', { params: search ? { search } : {} }),
  get: (id) => api.get(`/directors/${id}`),
  create: (data) => api.post('/directors', data),
  update: (id, data) => api.put(`/directors/${id}`, data),
  delete: (id) => api.delete(`/directors/${id}`),
  bulkImport: (file, opts = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    if (opts.sheet_name) fd.append('sheet_name', opts.sheet_name);
    if (opts.create_missing_clients) fd.append('create_missing_clients', 'true');
    return api.post('/directors/bulk-import', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Service Configs
export const serviceConfigsAPI = {
  list: () => api.get('/service-configs'),
  create: (data) => api.post('/service-configs', data),
  update: (id, data) => api.put(`/service-configs/${id}`, data),
  delete: (id) => api.delete(`/service-configs/${id}`),
  subtaskTitles: () => api.get('/subtask-titles'),
};

// Subtask Reminders
export const remindersAPI = {
  list: (params) => api.get('/subtask-reminders', { params }),
  create: (data) => api.post('/subtask-reminders', data),
  update: (id, data) => api.put(`/subtask-reminders/${id}`, data),
  delete: (id) => api.delete(`/subtask-reminders/${id}`),
  process: () => api.post('/subtask-reminders/process'),
};

// Boards
export const boardsAPI = {
  list: () => api.get('/boards'),
  get: (id) => api.get(`/boards/${id}`),
  create: (data) => api.post('/boards', data),
  update: (id, data) => api.put(`/boards/${id}`, data),
  delete: (id) => api.delete(`/boards/${id}`),
};

// Tasks
export const tasksAPI = {
  list: (params) => api.get('/tasks', { params }),
  get: (id) => api.get(`/tasks/${id}`),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
  addSubtask: (taskId, data) => api.post(`/tasks/${taskId}/subtasks`, data),
  addSubtasksBulk: (taskId, data) => api.post(`/tasks/${taskId}/subtasks/bulk`, data),
  updateSubtask: (taskId, subtaskId, data) => api.put(`/tasks/${taskId}/subtasks/${subtaskId}`, data),
  deleteSubtask: (taskId, subtaskId) => api.delete(`/tasks/${taskId}/subtasks/${subtaskId}`),
  updateSubtaskClientStatus: (taskId, subtaskId, clientId, data) =>
    api.put(`/tasks/${taskId}/subtasks/${subtaskId}/clients/${clientId}/status`, data),
  updateSubtaskClientPeriodStatus: (taskId, subtaskId, clientId, period, data, gstin) =>
    api.put(`/tasks/${taskId}/subtasks/${subtaskId}/clients/${clientId}/periods/${period}`, { ...data, gstin: gstin || undefined }),
  assignSubtaskStaff: (taskId, subtaskId, data) =>
    api.put(`/tasks/${taskId}/subtasks/${subtaskId}/assign`, data),
  assignClientStaff: (taskId, subtaskId, clientId, data, gstin) =>
    api.put(`/tasks/${taskId}/subtasks/${subtaskId}/clients/${clientId}/assign`, { ...data, gstin: gstin || undefined }),
  addClientNote: (taskId, subtaskId, clientId, data, gstin) =>
    api.post(`/tasks/${taskId}/subtasks/${subtaskId}/clients/${clientId}/notes`, { ...data, gstin: gstin || undefined }),
  addNote: (taskId, data) => api.post(`/tasks/${taskId}/notes`, data),
  clone: (taskId) => api.post(`/tasks/${taskId}/clone`),
  updateChecklistItemStatus: (taskId, subtaskId, clientId, itemName, period, data) =>
    api.put(`/tasks/${taskId}/subtasks/${subtaskId}/clients/${clientId}/checklist/${encodeURIComponent(itemName)}/periods/${period}`, data),
  bulkUpdateChecklistItemStatus: (taskId, subtaskId, clientId, itemName, data) =>
    api.put(`/tasks/${taskId}/subtasks/${subtaskId}/clients/${clientId}/checklist/${encodeURIComponent(itemName)}/bulk`, data),
  renameChecklistItem: (taskId, subtaskId, data) =>
    api.put(`/tasks/${taskId}/subtasks/${subtaskId}/checklist-items/rename`, data),
  seedAccounting: () => api.post('/admin/seed-accounting'),
  // Phase 2 — One-Time GST Activity instances
  createOneTimeInstance: (taskId, subtaskId, data) =>
    api.post(`/tasks/${taskId}/subtasks/${subtaskId}/instances`, data),
  updateOneTimeInstance: (taskId, subtaskId, instanceId, data) =>
    api.put(`/tasks/${taskId}/subtasks/${subtaskId}/instances/${instanceId}`, data),
  deleteOneTimeInstance: (taskId, subtaskId, instanceId) =>
    api.delete(`/tasks/${taskId}/subtasks/${subtaskId}/instances/${instanceId}`),
  // Audit boards — per-client folder path
  updateClientFolderPath: (taskId, subtaskId, clientId, folder_path) =>
    api.put(`/tasks/${taskId}/subtasks/${subtaskId}/clients/${clientId}/folder-path`, { folder_path }),
  // Per-checklist-item note (Audit + Accounting boards)
  updateChecklistItemNote: (taskId, subtaskId, clientId, itemName, note) =>
    api.put(`/tasks/${taskId}/subtasks/${subtaskId}/clients/${clientId}/checklist/${encodeURIComponent(itemName)}/note`, { note }),
  // GST return-status sync from the GST portal (via provider)
  gstSyncPreview: (taskId, subtaskId) =>
    api.post(`/tasks/${taskId}/subtasks/${subtaskId}/gst-sync/preview`),
  gstSync: (taskId, subtaskId) =>
    api.post(`/tasks/${taskId}/subtasks/${subtaskId}/gst-sync`),
};

// Chat
export const chatAPI = {
  listGroups: () => api.get('/chat/groups'),
  getGroup: (id) => api.get(`/chat/groups/${id}`),
  createGroup: (data) => api.post('/chat/groups', data),
  deleteGroup: (id) => api.delete(`/chat/groups/${id}`),
  getMessages: (groupId, limit = 50, search = '', pinnedOnly = false) => api.get(`/chat/groups/${groupId}/messages`, { params: { limit, ...(search ? { search } : {}), ...(pinnedOnly ? { pinned_only: true } : {}) } }),
  sendMessage: (groupId, data) => api.post(`/chat/groups/${groupId}/messages`, data),
  deleteMessage: (groupId, messageId) => api.delete(`/chat/groups/${groupId}/messages/${messageId}`),
  reactToMessage: (groupId, messageId, emoji) => api.post(`/chat/groups/${groupId}/messages/${messageId}/react`, { emoji }),
  pinMessage: (groupId, messageId) => api.post(`/chat/groups/${groupId}/messages/${messageId}/pin`),
  getOrCreateTaskThread: (data) => api.post('/chat/task-thread', data),
  getClientStatus: (groupId) => api.get(`/chat/groups/${groupId}/client-status`),
};

// Files
export const filesAPI = {
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/files/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getInfo: (id) => api.get(`/files/${id}`),
};

// Fee Proposals
export const feesAPI = {
  list: (params) => api.get('/fees', { params }),
  create: (data) => api.post('/fees', data),
  action: (id, data) => api.put(`/fees/${id}/action`, data),
};

// Invoices
export const invoicesAPI = {
  list: (params) => api.get('/invoices', { params }),
  get: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
  downloadPdf: (id) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
  uninvoicedSubtasks: (params) => api.get('/uninvoiced-subtasks', { params }),
};

// Firms
export const firmsAPI = {
  list: () => api.get('/firms'),
  get: (id) => api.get(`/firms/${id}`),
  create: (data) => api.post('/firms', data),
  update: (id, data) => api.put(`/firms/${id}`, data),
  delete: (id) => api.delete(`/firms/${id}`),
  uploadLogo: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/firms/${id}/logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  logoUrl: (id) => `${api.defaults.baseURL}/firms/${id}/logo`,
};

// Service Descriptions
export const serviceDescAPI = {
  list: () => api.get('/service-descriptions'),
  create: (data) => api.post('/service-descriptions', data),
  delete: (id) => api.delete(`/service-descriptions/${id}`),
};

// Client Fee Structure
export const clientFeesAPI = {
  list: (params) => api.get('/client-fees', { params }),
  create: (data) => api.post('/client-fees', data),
  update: (id, data) => api.put(`/client-fees/${id}`, data),
  delete: (id) => api.delete(`/client-fees/${id}`),
  lookup: (clientId, serviceName) => api.get('/client-fees/lookup', { params: { client_id: clientId, service_name: serviceName } }),
  downloadTemplate: () => api.get('/client-fees/template', { responseType: 'blob' }),
  bulkUpload: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/client-fees/bulk-upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// Payments
export const paymentsAPI = {
  list: (params) => api.get('/payments', { params }),
  record: (data) => api.post('/payments', data),
};

// Leads
export const leadsAPI = {
  create: (data) => api.post('/leads', data),
  list: () => api.get('/leads'),
};

// Notifications
export const notificationsAPI = {
  list: (params) => api.get('/notifications', { params }),
  unreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.post(`/notifications/${id}/mark-read`),
  markAllRead: () => api.post('/notifications/mark-all-read'),
  remove: (id) => api.delete(`/notifications/${id}`),
  clearRead: () => api.post('/notifications/clear-read'),
};

// Compliance Calendar
export const complianceAPI = {
  calendar: (params) => api.get('/compliance-calendar', { params }),
  reminders: () => api.get('/compliance-reminders'),
  sendReminder: (taskId) => api.post(`/tasks/${taskId}/send-reminder`),
  sendReminders: () => api.post('/compliance/send-reminders'),
  summary: (fy) => api.get('/compliance-summary', { params: { fy } }),
};

// Dashboard
export const dashboardAPI = {
  stats: () => api.get('/dashboard/stats'),
  subtaskStatusReport: () => api.get('/dashboard/subtask-status-report'),
  getLayout: () => api.get('/dashboard/layout'),
  saveLayout: (data) => api.put('/dashboard/layout', data),
  myTasks: () => api.get('/staff/my-tasks'),
  upcomingDue: () => api.get('/reminders/upcoming-due'),
};

// Activity Logs
export const activityLogsAPI = {
  list: (params) => api.get('/activity-logs', { params }),
};

// Seed
export const seedAPI = {
  seed: () => api.post('/seed'),
  reseedTasks: () => api.post('/admin/reseed-tasks'),
};

export default api;
